import { useEffect, useState } from 'react';
import {
  changeOwnPassword,
  createNewUser,
  deleteUserApi,
  getMe,
  listUsers,
  resetUserPassword,
  type AuthUser,
  type UserPublic,
} from '../api';

export default function Users() {
  const [users, setUsers] = useState<UserPublic[] | null>(null);
  const [me, setMe] = useState<AuthUser | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [showOwnPw, setShowOwnPw] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserPublic | null>(null);

  const reload = async () => {
    try {
      const [res, meRes] = await Promise.all([listUsers(), getMe()]);
      setUsers(res.users);
      setMe(meRes.user);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const onDelete = async (u: UserPublic) => {
    if (!confirm(`Admin „${u.email}" wirklich entfernen?`)) return;
    try {
      await deleteUserApi(u.id);
      reload();
    } catch (e: any) {
      const msg =
        e.message === 'cant_delete_last_admin'
          ? 'Der letzte Admin kann nicht gelöscht werden.'
          : e.message;
      alert(`Fehler: ${msg}`);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Admin-Nutzer</h1>
          <p className="page-sub">Verwalte die Zugänge zum Bertsy-Admin-Panel.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={() => setShowOwnPw(true)}>
            🔑 Eigenes Passwort ändern
          </button>
          <button className="btn btn-primary" onClick={() => setShowNew(true)}>
            + Neuen Admin anlegen
          </button>
        </div>
      </div>

      {err && (
        <div className="card" style={{ color: '#D9534F', marginBottom: 16 }}>
          Fehler: <code>{err}</code>
        </div>
      )}

      <div className="card">
        {!users ? (
          <p className="page-sub" style={{ margin: 0 }}>Wird geladen…</p>
        ) : users.length === 0 ? (
          <p className="page-sub" style={{ margin: 0 }}>
            Keine Admins angelegt.
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#5F6B68', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8' }}>E-Mail</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 90 }}>Rolle</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 160 }}>Angelegt</th>
                <th style={{ padding: '12px 8px', borderBottom: '1px solid #DDE9E8', width: 220 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid #DDE9E8' }}>
                  <td style={{ padding: '12px 8px', fontWeight: 600 }}>
                    {u.email}
                    {me?.id === u.id && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: '#5F6B68', fontWeight: 400 }}>(Sie)</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <span style={{ background: '#00A099', color: '#fff', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
                      {u.rolle}
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px', color: '#5F6B68' }}>
                    {new Date(u.created_at).toLocaleString('de-DE')}
                  </td>
                  <td style={{ padding: '12px 8px', display: 'flex', gap: 4 }}>
                    {me?.id !== u.id && (
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12 }}
                        onClick={() => setResetTarget(u)}
                      >
                        🔑 Passwort
                      </button>
                    )}
                    <button
                      className="btn btn-ghost"
                      style={{ color: '#D9534F', fontSize: 12 }}
                      onClick={() => onDelete(u)}
                    >
                      🗑 Entfernen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNew && (
        <NewUserForm onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />
      )}
      {showOwnPw && (
        <OwnPasswordForm onClose={() => setShowOwnPw(false)} />
      )}
      {resetTarget && (
        <ResetPasswordForm
          target={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}
    </>
  );
}

function NewUserForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== password2) {
      setErr('Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await createNewUser(email, password);
      onSaved();
    } catch (e: any) {
      const msg =
        e.message === 'email_taken'
          ? 'Diese E-Mail-Adresse ist bereits vergeben.'
          : e.message === 'password_too_short'
          ? 'Passwort muss mindestens 8 Zeichen lang sein.'
          : e.message === 'invalid_email'
          ? 'Bitte gültige E-Mail-Adresse eingeben.'
          : e.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,19,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: '#fff', padding: 32, borderRadius: 12,
          width: 380, maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 16px' }}>Neuen Admin anlegen</h2>
        <div className="field">
          <label>E-Mail</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus required />
        </div>
        <div className="field">
          <label>Passwort (mindestens 8 Zeichen)</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required />
        </div>
        <div className="field">
          <label>Passwort wiederholen</label>
          <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} required />
          {err && <div className="error">{err}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Speichern…' : 'Anlegen'}
          </button>
        </div>
      </form>
    </div>
  );
}

function OwnPasswordForm({ onClose }: { onClose: () => void }) {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== newPw2) {
      setErr('Neue Passwörter stimmen nicht überein.');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await changeOwnPassword(oldPw, newPw);
      setSaved(true);
      setTimeout(onClose, 1500);
    } catch (e: any) {
      const msg =
        e.message === 'old_password_wrong'
          ? 'Altes Passwort ist nicht korrekt.'
          : e.message === 'password_too_short'
          ? 'Neues Passwort muss mindestens 8 Zeichen lang sein.'
          : e.message === 'service_token_cannot_change_password'
          ? 'Service-Token können kein Passwort ändern.'
          : e.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,19,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: '#fff', padding: 32, borderRadius: 12,
          width: 380, maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 16px' }}>Eigenes Passwort ändern</h2>
        <div className="field">
          <label>Aktuelles Passwort</label>
          <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} autoFocus required autoComplete="current-password" />
        </div>
        <div className="field">
          <label>Neues Passwort (mindestens 8 Zeichen)</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} required autoComplete="new-password" />
        </div>
        <div className="field">
          <label>Neues Passwort wiederholen</label>
          <input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} minLength={8} required autoComplete="new-password" />
        </div>
        {err && <div className="error">{err}</div>}
        {saved && <div style={{ color: '#2E7D32', fontSize: 13, marginTop: 8 }}>✓ Passwort geändert</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={loading || saved}>
            {loading ? 'Speichern…' : 'Ändern'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordForm({ target, onClose }: { target: UserPublic; onClose: () => void }) {
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== newPw2) {
      setErr('Passwörter stimmen nicht überein.');
      return;
    }
    if (!confirm(`Passwort für „${target.email}" wirklich zurücksetzen? Der User muss sich danach mit dem neuen Passwort anmelden.`)) {
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await resetUserPassword(target.id, newPw);
      alert(`Passwort für ${target.email} wurde zurückgesetzt.`);
      onClose();
    } catch (e: any) {
      const msg =
        e.message === 'password_too_short'
          ? 'Passwort muss mindestens 8 Zeichen lang sein.'
          : e.message;
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,19,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: '#fff', padding: 32, borderRadius: 12,
          width: 380, maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <h2 style={{ margin: '0 0 8px' }}>Passwort zurücksetzen</h2>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#5F6B68' }}>
          Für: <strong>{target.email}</strong>
        </p>
        <div className="field">
          <label>Neues Passwort (mindestens 8 Zeichen)</label>
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} minLength={8} required autoFocus autoComplete="new-password" />
        </div>
        <div className="field">
          <label>Neues Passwort wiederholen</label>
          <input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} minLength={8} required autoComplete="new-password" />
        </div>
        {err && <div className="error">{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Speichern…' : 'Zurücksetzen'}
          </button>
        </div>
      </form>
    </div>
  );
}
